import { useState } from "react";
import axios, { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { useLocation } from 'react-router-dom';
import { API_URL } from "../environment";
import { getRefreshAuthToken, setAuthToken, removeAuthToken, removeRefreshAuthToken, setRefreshAuthToken, hasRefreshAuthToken } from "../common/Utils";
import { API } from "../common/Constants";

// Add a static promise to track ongoing refresh operations
let refreshTokenPromise: Promise<string> | null = null;

interface FetchCall {
  data: any
  status: number | null
  isLoading: boolean
  error: any
  callFetch: (endpoint: string,
       method: 'POST' | 'GET' | 'DELETE' | 'PUT', 
       headers:any, 
       query:any, 
       body:any) => Promise<any>
}
    // Array of regex patterns for endpoints that should skip 401 redirect
const SKIP_401_REDIRECT_PATTERNS: RegExp[] = [
        /^tenants\/.+\/info(\?.*)?$/  // Match pattern tenants/*/info where * is any tenant value
];

const useFetch = ():FetchCall => {
 
    const navigate = useNavigate();
    const location = useLocation()

    const [data,setData] = useState<any>(null);
    const [status,setStatus] = useState<number | null>(null);
    const [isLoading,setIsLoading] = useState<boolean>(false);
    const [error,setError] = useState<any>(null);


    // Check if endpoint should skip redirect on 401
    const shouldSkip401Redirect = (endpoint: string): boolean => {
        return SKIP_401_REDIRECT_PATTERNS.some((pattern: RegExp) => pattern.test(endpoint));
    };

    const refreshToken = async (refreshToken: string | null): Promise<string> => {
        // If there's already a refresh operation in progress, return that promise
        if (refreshTokenPromise) {
            return refreshTokenPromise;
        }
        
        if (!refreshToken) {
            throw new Error("No refresh token available");
        }

        // Create a new promise for the refresh token operation
        refreshTokenPromise = new Promise<string>(async (resolve, reject) => {
            try {
                const response = await axios.post(`${API_URL}/${API.REFRESH_TOKEN}`, {
                    refreshToken
                },{
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `${refreshToken}`
                    }
                });
                
                if (response.data) {
                    setAuthToken(response.data.token);
                    setRefreshAuthToken(response.data.refreshToken);
                    resolve(response.data.token);
                } else {
                    reject(new Error("Failed to refresh token"));
                }
            } catch (error) {
                removeAuthToken();
                removeRefreshAuthToken();
                reject(error);
            } finally {
                // Clear the promise once completed (success or failure)
                setTimeout(() => {
                    refreshTokenPromise = null;
                }, 0);
            }
        });

        return refreshTokenPromise;
    };

    const fetchData = async (endpoint:string, method:string, headers:any, query:any, body:any): Promise<any> => {
        setIsLoading(true)
        const options = {
          method: method ? method : 'GET',
          url: `${API_URL}/${endpoint}`,
          headers: {
            'Content-Type': 'application/json',
            ...headers
          },
          params: {
            ...query
          },
          data: body
        };
        try {
            const response = await axios.request(options);
            setData(response.data);
            setStatus(response.status)

            return response.data

        } catch (error:any) {
            if (axios.isAxiosError(error)) {
              const axiosError = error as AxiosError;

              // Handle 401 with token refresh
              if (axiosError.response?.status === 401 && hasRefreshAuthToken()) {
                try {
                    // Try to refresh the token
                    const refToken = getRefreshAuthToken();
                    const newToken = await refreshToken(refToken);
                    
                    // Update the headers with the new token
                    const newHeaders = {
                        ...headers,
                        Authorization: newToken
                    };
                    
                    // Retry the original request with the new token
                    return await fetchData(endpoint, method, newHeaders, query, body);
                } catch (refreshError) {
                    // If refresh fails, redirect to login (unless endpoint should skip)
                    if (location.pathname !== '/login' && !shouldSkip401Redirect(endpoint)) {
                        navigate('/login?path=' + location.pathname + location.search);
                    }
                    setError(axiosError);
                    setStatus(axiosError.response?.status || null);
                    throw error;
                }
              } else {
                setError(axiosError);
                setStatus(axiosError.response?.status || null);
                
                if (axiosError.response?.status === 401 && location.pathname !== '/login' && !shouldSkip401Redirect(endpoint)) {
                  navigate('/login?path=' + location.pathname + location.search);
                }
              }
            } else {
              setError(error);
            }
            throw error;
        } finally {
            setIsLoading(false)
        }
    };

    const callFetch = (endpoint:string, method:string, headers:any, query:any, body:any): Promise<any> => {
        setData(null)
        setStatus(null)
        setError(null)
        setIsLoading(true)
        return fetchData(endpoint, method, headers, query, body);
    }

    return { data, status, isLoading, error, callFetch };
}

export default useFetch;